import React, { useEffect, useRef, useState } from "react";
import styles from "./AddPurchaseOrderPanel.module.css";
import WebService from "../Services/WebService";
import { FaPlus, FaTrash } from "react-icons/fa";
import CrossButton from "../Customs/Button/CrossButton";
import AddVendorPanel from "../VendorManagement/AddVendorPanel";
import { toast } from "react-toastify";

// --- Types ---
interface Vendor {
  vendor_id: number;
  user_id: number;
  company_name: string;
}
interface RawMaterial {
  id: number;
  name: string;
  measuring_unit: string;
  measuring_unit_value: string | number;
  cost_price?: number;
}
interface RawCategory {
  category_id: number;
  category_name: string;
}
interface MaterialRow {
  categorySearch: string;
  selectedCategory: number | "";
  categoryOptions: RawCategory[];
  categoryLoading: boolean;
  product_id: number | null;
  product_name?: string;
  quantity: number | "";
  purchase_price: number | string;
  /** Signed other charges on this line (+ adds, − reduces) with a description. */
  additional_charge: string;
  additional_charge_note: string;
  measuring_unit?: string;
  measuring_unit_value?: string | number;
  rawSearch?: string;
  rawOptions?: RawMaterial[];
  rawLoading?: boolean;
  showCatDropdown?: boolean;
  showRawDropdown?: boolean;
}
interface AddPurchaseOrderPanelProps {
  open: boolean;
  onClose: () => void;
  mode?: "add" | "edit";
  editData?: {
    purchase_order_id: number;
    vendor_id: number;
    expected_delivery_date?: string;
    raw_materials: {
      product_id: number;
      product_name?: string;
      quantity: number;
      purchase_price?: number;
      additional_charge?: number | null;
      additional_charge_note?: string | null;
      remarks?: string;
      category_id?: number;
      category_name?: string;
      measuring_unit?: string;
      measuring_unit_value?: string | number;
    }[];
  };
}

const initialMaterialRow = (): MaterialRow => ({
  categorySearch: "",
  selectedCategory: "",
  categoryOptions: [],
  categoryLoading: false,
  product_id: null,
  product_name: "",
  quantity: "",
  purchase_price: "",
  additional_charge: "",
  additional_charge_note: "",
  measuring_unit: "",
  measuring_unit_value: "",
  rawSearch: "",
  rawOptions: [],
  rawLoading: false,
  showCatDropdown: false,
  showRawDropdown: false,
});

const AddPurchaseOrderPanel: React.FC<AddPurchaseOrderPanelProps> = ({
  open,
  onClose,
  mode = "add",
  editData,
}) => {
  // Vendor states
  const [vendorList, setVendorList] = useState<Vendor[]>([]);
  const [vendorSearch] = useState("");
  const [, setVendorLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<number | "">("");

  // Raw Material Rows
  const [materialRows, setMaterialRows] = useState<MaterialRow[]>([
    initialMaterialRow(),
  ]);
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [submitAttempt, setSubmitAttempt] = useState(false);

  // Other states
  const [expectedDelivery, setExpectedDelivery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showVendorPanel, setShowVendorPanel] = useState(false);
  const [vendorRefreshKey, setVendorRefreshKey] = useState(0);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const panelRef = useRef<HTMLDivElement>(null);

  // --- EDIT MODE: Pre-populate on open ---
  useEffect(() => {
    if (open && mode === "edit" && editData) {
      setSelectedVendor(editData.vendor_id || "");   
      let dateStr = "";
      if (editData.expected_delivery_date) {
        const d = new Date(editData.expected_delivery_date);
        if (!isNaN(d.getTime())) {
          dateStr = d.toISOString().slice(0, 10);
        }
      }
      setExpectedDelivery(dateStr);
      setMaterialRows(
        (editData.raw_materials || []).map((rm) => ({
          categorySearch: rm.category_name || "",
          selectedCategory: typeof rm.category_id === "number" ? rm.category_id : "",
          categoryOptions: rm.category_id && rm.category_name
            ? [{ category_id: rm.category_id, category_name: rm.category_name }]
            : [],
          categoryLoading: false,
          product_id: rm.product_id ?? null,
          product_name: rm.product_name || "",
          quantity: rm.quantity ?? "",
          purchase_price: rm.purchase_price ?? "",
          additional_charge: rm.additional_charge != null ? String(rm.additional_charge) : "",
          additional_charge_note: rm.additional_charge_note || "",
          measuring_unit: rm.measuring_unit || "",
          measuring_unit_value: rm.measuring_unit_value || "",
          rawSearch: rm.product_name || "",
          rawOptions: [],
          rawLoading: false,
          showCatDropdown: false,
          showRawDropdown: false,
        }))
      );
    } else if (open && mode === "add") {
      setSelectedVendor("");
      setExpectedDelivery("");
      setMaterialRows([initialMaterialRow()]);
    }
  }, [open, mode, editData]);

  // Fetch vendor list
  useEffect(() => {
    if (!open) return;
    setVendorLoading(true);
    WebService.getAPI({
      action: `/v1/door-management/vendors?user_like=${vendorSearch || ""}`,
    })
      .then((res: any) => setVendorList(res.data?.vendors || []))
      .catch(() => setVendorList([]))
      .finally(() => setVendorLoading(false));
  }, [vendorSearch, open, vendorRefreshKey]);
  const fetchCategories = (idx: number, search: string) => {
    setMaterialRows((prev) =>
      prev.map((row, i) =>
        i === idx ? { ...row, categoryLoading: true } : row
      )
    );
    WebService.getAPI({
      action: `/v1/door-management/raw-material/categories?type=NO_SUB_CATEGORY&like=${search || ""}`,
    })
      .then((res: any) => {
        const arr = Array.isArray(res.data) ? res.data : [];
        setMaterialRows((prev) =>
          prev.map((row, i) => {
            if (i !== idx) return row;
            let merged = arr;
            if (
              row.selectedCategory &&
              row.categorySearch &&
              !arr.some((cat: RawCategory) => cat.category_id === row.selectedCategory)
            ) {
              merged = [
                { category_id: row.selectedCategory, category_name: row.categorySearch },
                ...arr,
              ];
            }
            return { ...row, categoryOptions: merged, categoryLoading: false };
          })
        );
      })
      .catch(() =>
        setMaterialRows((prev) =>
          prev.map((row, i) =>
            i === idx ? { ...row, categoryOptions: [], categoryLoading: false } : row
          )
        )
      );
  };
  const fetchRawMaterials = (idx: number, search: string) => {
    setMaterialRows((prev) =>
      prev.map((row, i) =>
        i === idx ? { ...row, rawLoading: true } : row
      )
    );
    const catId = materialRows[idx].selectedCategory;
    let api = "/v1/door-management/raw-material?status=ACTIVE";
    if (catId) {
      api += `&category_id=${catId}`;
    } else {
      api += `&type=NO_SUB_CATEGORY`;
    }
    api += `&rm_like=${search}&limit=20&page_number=1`;

    WebService.getAPI({
      action: api
    })
      .then((res: any) => {
        const rawOptions = (res.data?.raw_materials || []).map((rm: any) => ({
          id: rm.id,
          name: rm.name,
          measuring_unit: rm.measuring_unit,
          measuring_unit_value: rm.measuring_unit_value,
          cost_price: rm.cost_price,
        }));
        setMaterialRows((prev) =>
          prev.map((row, i) =>
            i === idx ? { ...row, rawOptions, rawLoading: false } : row
          )
        );
      })
      .catch(() =>
        setMaterialRows((prev) =>
          prev.map((row, i) =>
            i === idx ? { ...row, rawOptions: [], rawLoading: false } : row
          )
        )
      );
  };

  function toApiDateString(dateStr: string) {
    return dateStr + "T00:00:00.000000+05:30";
  }
  const handleCatSearchChange = (idx: number, value: string) => {
    setMaterialRows((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              categorySearch: value,
              showCatDropdown: true,
              selectedCategory: "",
              rawSearch: "",
              product_id: null,
              product_name: "",
              measuring_unit: "",
              measuring_unit_value: "",
              purchase_price: "",
              rawOptions: [],
            }
          : row
      )
    );
    fetchCategories(idx, value);
  };
  const handleCategorySelect = (idx: number, cat: RawCategory) => {
    setMaterialRows((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              selectedCategory: cat.category_id,
              categorySearch: cat.category_name,
              showCatDropdown: false,
              rawSearch: "",
              product_id: null,
              product_name: "",
              measuring_unit: "",
              measuring_unit_value: "",
              purchase_price: "",
              rawOptions: [],
            }
          : row
      )
    );
    setFormErrors((prev) => {
      const newErr = { ...prev };
      delete newErr[`category_${idx}`];
      return newErr;
    });
  };
  const handleRawSearchChange = (idx: number, value: string) => {
    setMaterialRows((prev) =>
      prev.map((row, i) =>
        i === idx ? { ...row, rawSearch: value, showRawDropdown: true } : row
      )
    );
    fetchRawMaterials(idx, value);
  };
  const handleRawSelect = (idx: number, rm: RawMaterial) => {
    setMaterialRows((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              product_id: rm.id,
              product_name: rm.name,
              measuring_unit: rm.measuring_unit,
              measuring_unit_value: rm.measuring_unit_value,
              purchase_price:
                rm.cost_price != null && rm.cost_price !== undefined && !Number.isNaN(Number(rm.cost_price))
                  ? Number(rm.cost_price)
                  : row.purchase_price,
              rawSearch: rm.name,
              showRawDropdown: false,
            }
          : row
      )
    );
  };
  const handleQtyChange = (idx: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    setMaterialRows((prev) =>
      prev.map((row, i) =>
        i === idx ? { ...row, quantity: value === "" ? "" : Number(value) } : row
      )
    );
  };
  const handlePurchasePriceChange = (idx: number, value: string) => {
    if (value !== "" && !/^\d*\.?\d{0,2}$/.test(value)) return;
    setMaterialRows((prev) =>
      prev.map((row, i) =>
        i === idx ? { ...row, purchase_price: value === "" ? "" : value } : row
      )
    );
  };
  const updateRowCharge = (idx: number, patch: Partial<MaterialRow>) => {
    setMaterialRows((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };
  // Live total: qty × price + other charges on each line (same rule as the backend).
  const orderTotal = materialRows.reduce(
    (sum, row) =>
      sum + (Number(row.quantity) || 0) * (Number(row.purchase_price) || 0) + (Number(row.additional_charge) || 0),
    0
  );
  const handleAddMaterial = () => {
    setMaterialRows((prev) => [initialMaterialRow(), ...prev]);
  };
  const handleRemoveMaterial = (idx: number) => {
    setMaterialRows((prev) => prev.filter((_, i) => i !== idx));
  };

  // Date
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpectedDelivery(e.target.value);
  };

  // Add vendor success
  const handleVendorAddSuccess = () => {
    setShowVendorPanel(false);
    setVendorRefreshKey((c) => c + 1);
  };

  // Close when clicking the dimmed overlay (not the panel). Avoids document-level
  // click listeners that race with the "Add Purchase Order" button and native <select>.
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (showVendorPanel) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // --- Validation ---
 const validateForm = (forceAll: boolean = false) => {
  const errors: { [key: string]: string } = {};
  if (!selectedVendor) errors.vendor = "Vendor is required.";
  if (!expectedDelivery) errors.expectedDelivery = "Expected delivery date required.";
  if (materialRows.length === 0) errors.materials = "At least one material required.";
  materialRows.forEach((row, idx) => {
    if (!row.product_id)
      errors[`material_${idx}`] = "Select a raw material.";
    if (row.quantity === "" || row.quantity === 0)
      errors[`qty_${idx}`] = "Enter a valid quantity.";
    if (row.purchase_price === "" || row.purchase_price === null
        || Number.isNaN(Number(row.purchase_price)) || Number(row.purchase_price) < 0)
      errors[`price_${idx}`] = "Enter a valid purchase price.";
    const extra = row.additional_charge.trim();
    if (extra !== "" && !Number.isFinite(Number(extra)))
      errors[`charge_${idx}`] = "Enter a valid amount.";
    else if (Number(extra) !== 0 && !row.additional_charge_note.trim())
      errors[`charge_${idx}`] = "Add a description for the charge.";
  });
  return errors;
};


  // --- Submit (Add/Edit) ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempt(true);
    const errors = validateForm(true);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "edit" && editData?.purchase_order_id) {
        await WebService.putAPI({
          action: `/v1/door-management/purchase-order/${editData.purchase_order_id}`,
          body: {
            vendor_id: selectedVendor,
            raw_materials: materialRows.map((row) => ({
              product_id: row.product_id,
              quantity: row.quantity,
              purchase_price: Number(row.purchase_price),
              additional_charge: Number(row.additional_charge) || 0,
              additional_charge_note: row.additional_charge_note.trim(),
              remarks: "",
            })),
            expected_delivery_date: toApiDateString(expectedDelivery),
          },
        });
        toast.success("Purchase order updated!");
      } else {
        await WebService.postAPI({
          action: "/v1/door-management/purchase-order",
          body: {
            vendor_id: selectedVendor,
            raw_materials: materialRows.map((row) => ({
              product_id: row.product_id,
              quantity: row.quantity,
              purchase_price: Number(row.purchase_price),
              additional_charge: Number(row.additional_charge) || 0,
              additional_charge_note: row.additional_charge_note.trim(),
              remarks: "",
            })),
            expected_delivery_date: toApiDateString(expectedDelivery),
          },
        });
        toast.success("Purchase order submitted!");
      }
      onClose();
    } catch (err: any) {
      // toast.error("Failed to submit order. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // ================= RENDER ==================
  return (
    <>
      <div className={styles.overlay} onClick={handleOverlayClick}>
        <div
          className={styles.panel}
          ref={panelRef}
          onClick={(e) => e.stopPropagation()}
        >
          {/* --- Header --- */}
          <div className={styles.header}>
            <span>{mode === "edit" ? "Edit Purchase Order" : "Create Purchase Order"}</span>
            <CrossButton onClick={onClose} className={styles.closeBtn} />
          </div>

          <form className={styles.formWrapper} onSubmit={handleSubmit} autoComplete="off">
            {/* --- Vendor --- */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Select Information</div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Select Vendor</label>
                <div style={{ display: "flex", gap: 12 }}>
                  <select
                    className={`${styles.select} ${formErrors.vendor && (submitAttempt || touched.vendor) ? styles.inputError : ""}`}
                    value={selectedVendor === "" ? "" : String(selectedVendor)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedVendor(value === "" ? "" : Number(value));
                      setTouched((prev) => ({ ...prev, vendor: true }));
                      setFormErrors((prev) => {
                        const next = { ...prev };
                        delete next.vendor;
                        return next;
                      });
                    }}
                    onBlur={() => setTouched((prev) => ({ ...prev, vendor: true }))}
                    onMouseDown={(e) => e.stopPropagation()}
                    required
                    style={{ minWidth: 210 }}
                  >
                    <option value="">--Select Vendor--</option>
                    {vendorList.map((vendor) => {
                      const id = vendor.vendor_id ?? vendor.user_id;
                      return (
                        <option key={id} value={String(id)}>
                          {vendor.company_name}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    className={styles.addVendorBtn}
                    onClick={() => setShowVendorPanel(true)}
                  >
                    + Add New Vendor
                  </button>
                </div>
                {formErrors.vendor && (submitAttempt || touched.vendor) && (
                  <div className={styles.errorMsg}>{formErrors.vendor}</div>
                )}
              </div>
            </div>

            {/* --- Materials (Dynamic Categories/Material) --- */}
            <div className={styles.section} style={{ marginTop: 24 }}>
              <div className={styles.sectionTitle} style={{ marginBottom: 0 }}>
                Materials
              </div>
              <button
                type="button"
                className={styles.addMaterialBtn}
                onClick={handleAddMaterial}
                style={{ marginBottom: 15 }}
              >
                <FaPlus /> Add Material
              </button>
              <div className={styles.materialList}>
                {materialRows.map((row, idx) => (
                  <div className={styles.materialRow} key={idx}>
                    <div className={styles.materialRowHeader}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span>Material #{materialRows.length - idx}</span>
                        {row.product_id && row.measuring_unit && row.measuring_unit_value && (
                          <span className={styles.unitPreviewChip}>
                            A Quantity: <b>{row.measuring_unit_value} {row.measuring_unit}</b>
                          </span>
                        )}
                      </div>
                      {materialRows.length > 1 && (
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => handleRemoveMaterial(idx)}
                          tabIndex={-1}
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>

                    <div className={styles.materialFields}>
                      {/* --- Category Dropdown --- */}
                      <div style={{ position: "relative", flex: 2, minWidth: 150 }}>
                        <label className={styles.label}>Material Category</label>
                        <input
                          className={styles.input}
                          value={row.selectedCategory ? row.categorySearch : row.categorySearch}
                          onChange={e => handleCatSearchChange(idx, e.target.value)}
                          onFocus={() => {
                            setMaterialRows(prev =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, showCatDropdown: true } : { ...r, showCatDropdown: false }
                              )
                            );
                            fetchCategories(idx, row.categorySearch);
                          }}
                          placeholder="Search Category..."
                          autoComplete="off"
                        />
                        {row.showCatDropdown && (
                          <div className={styles.dropdownBox} style={{ zIndex: 15, position: "absolute", top: 70, left: 0, maxWidth: 320 }}>
                            {row.categoryLoading ? (
                              <div className={styles.dropdownLoading}>Loading...</div>
                            ) : row.categoryOptions.length > 0 ? (
                              row.categoryOptions.map((cat) => (
                                <div
                                  className={styles.dropdownItem}
                                  key={cat.category_id}
                                  onMouseDown={e => {
                                    e.stopPropagation();
                                    setTimeout(() => handleCategorySelect(idx, cat), 0);
                                  }}
                                >
                                  {cat.category_name}
                                </div>
                              ))
                            ) : (
                              <div className={styles.dropdownEmpty}>No categories found.</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* --- Material Dropdown --- */}
                     <div style={{ position: "relative", flex: 3, minWidth: 200 }}>
  <label className={styles.label}>Material Name</label>
  <input
    className={`${styles.input} ${
      formErrors[`material_${idx}`] && (submitAttempt || touched[`material_${idx}`])
        ? styles.inputError
        : ""
    }`}
    value={row.rawSearch || ""}
    onFocus={() => {
      setMaterialRows(prev =>
        prev.map((r, i) =>
          i === idx ? { ...r, showRawDropdown: true } : { ...r, showRawDropdown: false }
        )
      );
      fetchRawMaterials(idx, row.rawSearch || "");
    }}
    onChange={e => handleRawSearchChange(idx, e.target.value)}
    onBlur={() => setTouched((prev) => ({ ...prev, [`material_${idx}`]: true }))}
    placeholder="Search Raw Material..."
    autoComplete="off"
    required
  />
  {row.showRawDropdown && (
    <div className={styles.dropdownBox} style={{ zIndex: 14, position: "absolute", top: 70, left: 0 }}>
      {row.rawLoading ? (
        <div className={styles.dropdownLoading}>Loading...</div>
      ) : (row.rawOptions && row.rawOptions.length > 0) ? (
        row.rawOptions.map((opt) => (
          <div
            className={styles.dropdownItem}
            key={opt.id}
            onMouseDown={e => {
              e.stopPropagation();
              setTimeout(() => handleRawSelect(idx, opt), 0);
            }}
            tabIndex={-1}
          >
            {opt.name}
          </div>
        ))
      ) : (
        <div className={styles.dropdownEmpty}>No raw materials found.</div>
      )}
    </div>
  )}
  {formErrors[`material_${idx}`] && (submitAttempt || touched[`material_${idx}`]) && (
    <div className={styles.errorMsg}>{formErrors[`material_${idx}`]}</div>
  )}
</div>


                      {/* --- Quantity --- */}
                      <div style={{ flex: 1, minWidth: 80 }}>
                        <label className={styles.label}>Quantity</label>
                        <input
                          type="number"
                          min={1}
                          className={styles.input}
                          value={row.quantity}
                          onChange={e => handleQtyChange(idx, e.target.value)}
                          required
                          placeholder="0"
                        />
                        {formErrors[`qty_${idx}`] && (
                          <div className={styles.errorMsg}>{formErrors[`qty_${idx}`]}</div>
                        )}
                      </div>
                      <div style={{ flex: 1.2, minWidth: 110 }}>
                        <label className={styles.label}>Purchase Price</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${styles.input} ${formErrors[`price_${idx}`] ? styles.inputError : ""}`}
                          value={row.purchase_price}
                          onChange={e => handlePurchasePriceChange(idx, e.target.value)}
                          required
                          placeholder="0.00"
                        />
                        {formErrors[`price_${idx}`] && (
                          <div className={styles.errorMsg}>{formErrors[`price_${idx}`]}</div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                      <div style={{ flex: 3, minWidth: 180 }}>
                        <label className={styles.label}>Other Charges (description)</label>
                        <input
                          type="text"
                          className={styles.input}
                          value={row.additional_charge_note}
                          maxLength={120}
                          onChange={e => updateRowCharge(idx, { additional_charge_note: e.target.value })}
                          placeholder="e.g. Transport, Cutting"
                        />
                      </div>
                      <div style={{ flex: 1.2, minWidth: 110 }}>
                        <label className={styles.label}>± Amount</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`${styles.input} ${formErrors[`charge_${idx}`] ? styles.inputError : ""}`}
                          value={row.additional_charge}
                          title="Positive adds to the total, negative reduces it"
                          onChange={e => {
                            const v = e.target.value;
                            if (v === "" || v === "-" || /^-?\d*\.?\d{0,2}$/.test(v)) updateRowCharge(idx, { additional_charge: v });
                          }}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    {formErrors[`charge_${idx}`] && (
                      <div className={styles.errorMsg}>{formErrors[`charge_${idx}`]}</div>
                    )}
                  </div>
                ))}
                {formErrors.materials && (
                  <div className={styles.errorMsg}>{formErrors.materials}</div>
                )}
              </div>
            </div>

            {/* --- Order Summary --- */}
            <div className={styles.section} style={{ marginTop: 28 }}>
              <div className={styles.sectionTitle} style={{ color: "#1E3A8A" }}>
                Order Summary
              </div>
              <div className={styles.summaryBox}>
                <div className={styles.summaryItem}>
                  <span>Total Materials:</span>
                  <span>{materialRows.length} items</span>
                </div>
                <div className={styles.summaryItem}>
                  <span>Order Total:</span>
                  <span style={{ fontWeight: 700 }}>
                    ₹{orderTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className={styles.summaryItem}>
                  <span>Expected Delivery Date:</span>
                  <input
                    type="date"
                    className={`${styles.input} ${formErrors.expectedDelivery && (submitAttempt || touched.expectedDelivery) ? styles.inputError : ""}`}
                    value={expectedDelivery}
                    onChange={handleDateChange}
                    onBlur={() => setTouched((prev) => ({ ...prev, expectedDelivery: true }))}
                    required
                    style={{ maxWidth: 180 }}
                  />
                  {formErrors.expectedDelivery && (submitAttempt || touched.expectedDelivery) && (
                    <div className={styles.errorMsg}>{formErrors.expectedDelivery}</div>
                  )}
                </div>
              </div>
            </div>

            {/* --- Submit --- */}
            <div className={styles.formFooter}>
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={!selectedVendor || submitting}
              >
                {submitting
                  ? mode === "edit"
                    ? "Updating..."
                    : "Submitting..."
                  : mode === "edit"
                    ? "Update Order"
                    : "Submit order"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Vendor Panel Overlay */}
      {showVendorPanel && (
        <AddVendorPanel
          onClose={() => setShowVendorPanel(false)}
          onSuccess={handleVendorAddSuccess}
        />
      )}
    </>
  );
};

export default AddPurchaseOrderPanel;
